import React from "react"

import { addWeeks, startOfWeek, subWeeks } from "date-fns"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { LoadingState } from "@/components/shared/LoadingState"
import { Button } from "@/components/ui/button"
import { formatDayMonth, formatDayMonthYear, formatDayNumeric } from "@/lib/dateFormats"
import { RouteWeeklyData } from "@/lib/weeklyPlan"

import WeekTable from "./WeekTable"
import { useWeekData } from "./useWeekData"

interface WeekPanelProps {
  weekStart: Date
  onWeekChange: (d: Date) => void
  compact?: boolean
  compactSummary?: boolean
  defaultCollapsed?: boolean
  onRouteClick?: (route: RouteWeeklyData, dateStr: string, dayLabel: string) => void
}

const WeekPanel: React.FC<WeekPanelProps> = ({
  weekStart,
  onWeekChange,
  compact,
  compactSummary,
  defaultCollapsed,
  onRouteClick,
}) => {
  const { weekEnd, weekDays, data: weekData, isLoading } = useWeekData(weekStart)

  return (
    <div className="flex flex-col gap-3 min-w-0 flex-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-palette-jungleGreen900">
            WC {formatDayNumeric(weekStart)}
          </h3>
          <span className="text-xs text-palette-jungleGreen500">
            {formatDayMonth(weekStart)} — {formatDayMonthYear(weekEnd)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onWeekChange(subWeeks(weekStart, 1))}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onWeekChange(startOfWeek(new Date(), { weekStartsOn: 1 }))}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onWeekChange(addWeeks(weekStart, 1))}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {isLoading ? (
        <LoadingState label="Loading…" />
      ) : (
        <div className="overflow-x-auto">
          <WeekTable
            weekDays={weekDays}
            data={weekData}
            compact={compact}
            compactSummary={compactSummary}
            defaultCollapsed={defaultCollapsed}
            onRouteClick={onRouteClick}
          />
        </div>
      )}
    </div>
  )
}

export default WeekPanel
